import { Alert } from "react-bootstrap";
import { useParams } from "react-router-dom";
import type { HouseholdsController } from "../../../../api/src/households/households.controller";
import type { UnpackResponse } from "../../../../api/src/lib";
import Loading from "../../Loading";
import AssetAllocationBarChart, {
  AssetAllocationSeries,
} from "../../components/AssetAllocationBarChart";
import {
  deserializeAllocationDetailsPercents,
  useAuthenticatedPercentFetch,
} from "../percents";
import { hasAccountsWithoutModels } from "../../lib/modelValidation";
import { calculateHouseholdExcludedSecuritiesPercentage } from "../../lib/excludedSecurities";
import { useQueryHousehold } from "./lib";

const HouseholdAssetAllocationChart = ({
  householdId: propHouseholdId,
}: {
  householdId?: number;
}) => {
  const { householdId: paramHouseholdId } = useParams();
  const householdId = propHouseholdId ?? paramHouseholdId;

  const { data: household } = useQueryHousehold(
    typeof householdId === "string"
      ? parseInt(householdId)
      : (householdId ?? 0),
    {
      includeAccounts: true,
      enabled: typeof householdId !== "undefined" && householdId !== null,
    },
  );

  const {
    isPending: isPendingAllocationDetails,
    isError: isErrorAllocationDetails,
    data: currentAllocationDetails,
  } = useAuthenticatedPercentFetch<
    UnpackResponse<HouseholdsController["getHouseholdAllocationDetails"]>
  >(
    `/v1/households/${householdId}/allocation-details`,
    deserializeAllocationDetailsPercents,
  );

  // Check if household has accounts without models (excluding $0 AUM accounts)
  const accountsWithoutModels = hasAccountsWithoutModels(
    household,
    household?.accounts ?? [],
  );

  // Calculate percentage of excluded securities across all household accounts
  const excludedSecuritiesPercentage = household?.accounts
    ? calculateHouseholdExcludedSecuritiesPercentage(household.accounts)
    : 0;

  const data: AssetAllocationSeries[] = [
    {
      label: "Current",
      data: currentAllocationDetails?.data.current ?? [],
    },
    ...(!accountsWithoutModels
      ? [
          {
            label: "Client Target",
            data: currentAllocationDetails?.data.target ?? [],
          },
        ]
      : []),
  ];

  return isPendingAllocationDetails ? (
    <Loading message="Asset Allocation" />
  ) : isErrorAllocationDetails ? (
    <Alert variant="danger">Failed to load Allocation Details</Alert>
  ) : accountsWithoutModels ? (
    <Alert variant="secondary" className="py-2 px-3">
      Target Allocation requires a household model or account model for every
      account.
    </Alert>
  ) : (currentAllocationDetails?.data.current.length ?? 0) === 0 &&
    (currentAllocationDetails?.data.target.length ?? 0) === 0 ? (
    <Alert variant="info">No Allocation Details</Alert>
  ) : (
    <AssetAllocationBarChart
      data={data}
      excludedSecuritiesPercentage={excludedSecuritiesPercentage}
    />
  );
};

export default HouseholdAssetAllocationChart;
